/**
 * Module-scope cache for `/api/widget/:slug/bootstrap` responses.
 *
 * Phase 5 — keyed by `(merchant, widgetSlug, themeSlug)`. 5-minute TTL.
 * Survives across `open` / `close` cycles within one page lifetime but
 * dies on hard reload — no localStorage involvement, no PII risk.
 *
 * The cache is read-through by WidgetApi.bootstrap(), not by App.tsx
 * directly. Single point of truth keeps imperative-mount and auto-mount
 * paths on the same caching behavior.
 */

import type { BootstrapResponse } from './types';

interface CacheEntry {
  data: BootstrapResponse;
  fetchedAt: number;
}

const TTL_MS = 5 * 60 * 1000;

const cache = new Map<string, CacheEntry>();

export function cacheKey(
  merchant: string,
  widgetSlug: string | null,
  themeSlug: string | null,
  // A `?service=` deep-link yields a distinct bootstrap (it carries an extra
  // unlisted service), so it must partition the cache.
  preselect: string | null = null,
): string {
  return `${merchant}::${widgetSlug ?? ''}::${themeSlug ?? ''}::${preselect ?? ''}`;
}

export function getCached(key: string): CacheEntry | undefined {
  return cache.get(key);
}

export function setCached(key: string, data: BootstrapResponse): void {
  cache.set(key, { data, fetchedAt: Date.now() });
}

export function isStale(entry: CacheEntry, ttlMs: number = TTL_MS): boolean {
  return Date.now() - entry.fetchedAt >= ttlMs;
}

/**
 * Cheap structural-ish comparison for bootstrap payloads. Used by the
 * stale-while-revalidate path to decide whether to fire `onRefresh`. False
 * positives (returns true when nothing meaningful changed) just trigger a
 * harmless re-dispatch in App.tsx; false negatives (returns true when the
 * catalog actually shifted) would suppress a needed hot-swap, so the
 * comparison errs toward "changed".
 */
export function bootstrapDiffers(
  a: BootstrapResponse,
  b: BootstrapResponse,
): boolean {
  if (a.services.length !== b.services.length) return true;
  if (a.staff.length !== b.staff.length) return true;
  for (let i = 0; i < a.services.length; i++) {
    if (a.services[i].id !== b.services[i].id) return true;
    if (a.services[i].price.amountCents !== b.services[i].price.amountCents) {
      return true;
    }
  }
  for (let i = 0; i < a.staff.length; i++) {
    if (a.staff[i].id !== b.staff[i].id) return true;
  }
  // P5.3 — the pay-over-time names. Absent on both sides for every tenant
  // without the Payment Element, so this never fires for them.
  if ((a.financingMethods ?? []).join(',') !== (b.financingMethods ?? []).join(',')) return true;
  return false;
}

/** Test-only / dev-only helper. Lets us blow away the cache between scenarios. */
export function clearCache(): void {
  cache.clear();
}
